import { InferType } from 'yup';

import { getFormattedDateString } from '@zeta-core';
import {
  IBeneficiaryBenefitsAndInsurance,
  IBenefitsAndInsuranceResponse,
  IEmployeeBenefitBenefitsAndInsurance,
  IInsuranceBenefitsAndInsurance,
  IOtherBenefitBenefitsAndInsurance,
} from '@zeta-types';
import {
  beneficiariesAndInsuranceValidationSchema,
  beneficiariesValidations,
  employeeBenefitsValidations,
  otherBenefitsValidations,
  policiesValidations,
} from './form-helpers';

type beneficiariesAndInsuranceValidationSchemaType = InferType<typeof beneficiariesAndInsuranceValidationSchema>;

export const mapResponseToSchema = (response: IBenefitsAndInsuranceResponse, beneficiariesList): beneficiariesAndInsuranceValidationSchemaType => {
  const mapBeneficiaries = (beneficiaries: IBeneficiaryBenefitsAndInsurance[] = []) => {
    return beneficiaries.map((beneficiary) => {
      const { id = '', relationship = '', dateOfBirth = null, percentage = null } = beneficiary || {};
      const nameData = beneficiariesList?.find((item) => item?.id === id || item?.personalPiiDetails?.firstName === beneficiary?.firstName) || {};

      const address =
        Array.isArray(beneficiary?.address) && beneficiary.address.length > 0
          ? beneficiary.address[0]
          : { addressLine1: '', addressLine2: '', city: '', country: '', postcode: '' };

      const { addressLine1 = '', addressLine2 = '', city = '', country = '', postcode = '' } = address;

      return {
        beneficiaryInsuranceID: beneficiary?.beneficiaryInsuranceID || '',
        id,
        name: nameData,
        relationship,
        dateOfBirth,
        addressLine1,
        addressLine2,
        city,
        country,
        postcode,
        percentage,
        show: true,
      };
    });
  };

  const mapInsurance = (insurance: IInsuranceBenefitsAndInsurance[] = []) => {
    return insurance.map((policy) => ({
      id: policy.id || '',
      institution: policy.providerName || '',
      premium: policy.premium || null,
      insuranceType: policy.insuranceType || '',
      sumAssured: policy.coverageAmount || null,
      deferredPeriodInWeeks: policy.deferredPeriodInWeeks || '',
      startDate: policy.startDate || '',
      endDate: policy.endDate || '',
      description: policy.description || '',
      beneficiaries: mapBeneficiaries(policy.beneficiary?.beneficiaries || []),
      show: true,
    }));
  };

  const mapEmployeeBenefits = (benefits: IEmployeeBenefitBenefitsAndInsurance[]) => {
    if (benefits) {
      return benefits.map((benefit) => ({
        id: benefit.id || '',
        institution: benefit.benefitProvider || '',
        premium: benefit.premium || null,
        policyNumber: benefit.policyNumber || '',
        insuranceType: benefit.benefitType || '',
        sumAssured: benefit.benefitValue || null,
        duration: benefit.duration || '',
        startDate: benefit.startDate || '',
        endDate: benefit.endDate || '',
        description: benefit.benefitDescription || '',
        show: true,
      }));
    }

    return [];
  };

  const mapOtherBenefits = (benefits: IOtherBenefitBenefitsAndInsurance[]) => {
    if (benefits) {
      return benefits.map((benefit) => ({
        id: benefit.id || '',
        benefitType: benefit.benefitType || '',
        providerName: benefit.providerName || '',
        frequency: benefit.frequency || '',
        isTaxable: benefit.taxable ? 'YES' : 'NO',
        benefitValue: benefit.benefitValue || null,
        startDate: benefit.startDate || '',
        endDate: benefit.endDate || '',
        description: benefit.benefitDescription || '',
        show: true,
      }));
    }

    return [];
  };

  return {
    client: {
      policies: mapInsurance(response?.employeeBenefits?.insurances?.clientInsurance),
      employeeBenefits: mapEmployeeBenefits(response?.employeeBenefits?.employeeBenefits?.clientBenefits),
      otherBenefits: mapOtherBenefits(response?.employeeBenefits?.otherBenefits?.clientBenefits),
    },
    partner: {
      policies: mapInsurance(response?.employeeBenefits?.insurances?.partnerInsurance),
      employeeBenefits: mapEmployeeBenefits(response?.employeeBenefits?.employeeBenefits?.partnerBenefits),
      otherBenefits: mapOtherBenefits(response?.employeeBenefits?.otherBenefits?.partnerBenefits),
    },
    joint: {
      policies: mapInsurance(response?.employeeBenefits?.insurances?.jointInsurance),
      employeeBenefits: mapEmployeeBenefits(response?.employeeBenefits?.employeeBenefits?.jointBenefits),
      otherBenefits: mapOtherBenefits(response?.employeeBenefits?.otherBenefits?.jointBenefits),
    },
  };
};

export const createPayloadBeneficiariesAndInsurance = (values: beneficiariesAndInsuranceValidationSchemaType) => {
  type beneficiariesValidationsType = InferType<typeof beneficiariesValidations>;
  function transformBeneficiaries(beneficiaries: beneficiariesValidationsType[] = []) {
    return beneficiaries
      .map((beneficiary) => {
        if (beneficiary?.name?.personalPiiDetails?.firstName && beneficiary?.name?.personalPiiDetails?.firstName.length > 0) {
          return {
            beneficiaryInsuranceID: beneficiary?.beneficiaryInsuranceID,
            id: beneficiary.name?.id,
            firstName: beneficiary.name?.personalPiiDetails?.firstName || '',
            lastName: beneficiary.name?.personalPiiDetails?.lastName || '',
            title: beneficiary.name?.personalPiiDetails?.title || '',
            name: `${beneficiary.name?.personalPiiDetails?.firstName} ${beneficiary.name?.personalPiiDetails?.lastName}`,
            percentage: beneficiary.percentage || 0,
            relationship: beneficiary.name?.relationship || '',
            dateOfBirth: getFormattedDateString(beneficiary?.name?.personalPiiDetails?.dateOfBirth, 'YYYY-MM-DD') || '',
            address: beneficiary?.name?.addresses,
          };
        }
        return undefined;
      })
      .filter(Boolean) as IBeneficiaryBenefitsAndInsurance[];
  }

  type policiesValidationsType = InferType<typeof policiesValidations>;

  function transformInsurance(policies: policiesValidationsType[]) {
    return policies.map((policy) => ({
      id: policy.id || '',
      providerName: policy.institution || '',
      premium: policy.premium || 0,
      insuranceType: policy.insuranceType || '',
      coverageAmount: policy.sumAssured || 0,
      deferredPeriodInWeeks: policy.deferredPeriodInWeeks || '',
      startDate: getFormattedDateString(policy.startDate, 'YYYY-MM-DD') || '',
      endDate: getFormattedDateString(policy.endDate, 'YYYY-MM-DD') || '',
      description: policy.description || '',
      beneficiary: { beneficiaries: transformBeneficiaries(policy.beneficiaries || []), insuranceBeneficiary: true },
    }));
  }

  type employeeBenefitsValidationsType = InferType<typeof employeeBenefitsValidations>;

  function transformEmployeeBenefits(employeeBenefits: employeeBenefitsValidationsType[]) {
    return employeeBenefits.map((benefit) => ({
      id: benefit.id || '',
      benefitProvider: benefit.institution || '',
      premium: benefit.premium || 0,
      policyNumber: benefit.policyNumber || '',
      benefitType: benefit.insuranceType || '',
      benefitValue: benefit.sumAssured || 0,
      duration: benefit.duration || '',
      startDate: getFormattedDateString(benefit.startDate, 'YYYY-MM-DD') || '',
      endDate: getFormattedDateString(benefit.endDate, 'YYYY-MM-DD') || '',
      benefitDescription: benefit.description || '',
    }));
  }

  type otherBenefitsValidationsType = InferType<typeof otherBenefitsValidations>;

  function transformOtherBenefits(otherBenefits: otherBenefitsValidationsType[]) {
    return otherBenefits.map((benefit) => ({
      id: benefit.id || '',
      benefitType: benefit.benefitType || '',
      providerName: benefit.providerName || '',
      frequency: benefit.frequency || '',
      taxable: benefit.isTaxable === 'Yes' ? true : false,
      benefitValue: benefit.benefitValue || 0,
      startDate: getFormattedDateString(benefit.startDate, 'YYYY-MM-DD') || '',
      endDate: getFormattedDateString(benefit.endDate, 'YYYY-MM-DD') || '',
      benefitDescription: benefit.description || '',
    }));
  }
  type beneficiariesAndInsuranceValidationSchemaType = InferType<typeof beneficiariesAndInsuranceValidationSchema>;

  function transformToPayload(values: beneficiariesAndInsuranceValidationSchemaType) {
    return {
      employeeBenefits: {
        clientBenefits: transformEmployeeBenefits(values.client.employeeBenefits || []),
        partnerBenefits: transformEmployeeBenefits(values.partner.employeeBenefits || []),
        jointBenefits: transformEmployeeBenefits(values.joint.employeeBenefits || []),
      },

      otherBenefits: {
        clientBenefits: transformOtherBenefits(values.client.otherBenefits || []),
        partnerBenefits: transformOtherBenefits(values.partner.otherBenefits || []),
        jointBenefits: transformOtherBenefits(values.joint.otherBenefits || []),
      },

      insurances: {
        clientInsurance: transformInsurance(values.client.policies || []),
        partnerInsurance: transformInsurance(values.partner.policies || []),
        jointInsurance: transformInsurance(values.joint.policies || []),
      },
    };
  }

  return transformToPayload(values);
};
